import { useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks/useAxiosClient";
import { useLocalization } from "@/hooks/useLocalizationContext";
import { useRegion } from "@/hooks/useRegion";
import { GetPanelsRequest } from "@/types/requests";
import { Panel } from "@/types/resources";
import { GetPanelsResponse } from "@/types/responses";

const PANELS_PER_PAGE = 20;

export const usePanels = () => {
  const { i18n } = useLocalization();
  const { region } = useRegion();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [panels, setPanels] = useState<Panel[]>([]);

  const client = useAxiosClient("/panels");

  // Re-enter the loading state during render (not in an effect) when the
  // region changes, so the UI shows loading while refetching.
  const [prevRegion, setPrevRegion] = useState(region);
  if (prevRegion !== region) {
    setPrevRegion(region);
    setLoading(true);
    setError(null);
  }

  // Without a folder_id, the endpoint returns every panel the current user
  // can access across all folders, capped at 20 per page.
  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      const all: Panel[] = [];
      for (let page = 1; ; page++) {
        const params: GetPanelsRequest = {
          items_per_page: PANELS_PER_PAGE,
          page,
        };
        const { data } = await client.get<GetPanelsResponse>("", { params });
        all.push(...data.panels);
        if (all.length >= data.count || data.panels.length === 0) break;
      }
      return all;
    };

    fetchAll()
      .then((all) => {
        if (cancelled) return;
        setPanels(all);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : i18n.t("errors.unknown"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client, i18n, region]);

  return { error, loading, panels };
};
